import React, { useMemo, useEffect , ReactNode } from 'react';
import { WMClassesType, WMMessage, WMTextField } from '@walkme/wm-ui';
import { Controller, useFormContext } from 'react-hook-form';
import _truncate from 'lodash/truncate';
import cc from 'classcat';

import styles from '../NotificationTypePicker.module.scss';
import runtimeFieldClasses from './RuntimeField.module.scss';

export const RuntimeFieldMaxLength = 200;

export const RuntimeField = ({
    label,
    value = '',
    description,
    invalidErrorMessage,
    maxLength = RuntimeFieldMaxLength,
    onChange: propsOnChange,
    dataTestid,
    tooltipText,
    multiline = false,
    minRows,
    overrideIcon,
    classes
}: {
    label: string;
    value?: string;
    description: string;
    invalidErrorMessage: string;
    maxLength?: number;
    onChange?: (value: string) => void;
    dataTestid?: string;
    tooltipText?: ReactNode;
    multiline?: boolean;
    minRows?: number;
    overrideIcon?: ReactNode;
    classes?: WMClassesType<'message'>;
}) => {
    const { clearErrors, setValue } = useFormContext();

    useEffect(() => {
        if (!value) return;
        const shortenedValue = _truncate(value, {
            length: RuntimeFieldMaxLength,
            separator: /(,|\.)? +/
        });
        setValue(label, shortenedValue);
    }, [label, setValue, value]);

    const TextFieldLabel = useMemo(
        () =>
            tooltipText ? (
                <WMMessage
                    className={styles.runtimeFieldLabel}
                    classes={{ message: cc([styles.runtimeFieldMessage, classes?.message]) }}
                    message={label}
                    iconPosition="end"
                    overrideIcon={overrideIcon}
                    tooltipProps={{
                        title: tooltipText,
                        arrow: true
                    }}
                />
            ) : (
                label
            ),
        [classes?.message, label, overrideIcon, tooltipText]
    );

    return (
        <Controller
            name={label}
            defaultValue={(value || '').substr(0, maxLength) || ''}
            rules={{ validate: value => !!value }}
            render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                <WMTextField
                    ds2
                    maxLength={maxLength}
                    inputProps={{ 'data-testid': dataTestid } as $TSFixMe}
                    value={value ?? ''}
                    {...(!multiline ? { size: 'large' } : {})}
                    showCounter
                    status={invalid ? 'error' : 'default'}
                    helperText={invalid && invalidErrorMessage}
                    multiline={multiline}
                    {...(multiline && minRows ? { minRows } : {})}
                    label={TextFieldLabel}
                    placeholder={description}
                    onChange={newValue => {
                        clearErrors(label);
                        onChange(newValue.currentTarget.value);
                        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
                        propsOnChange(newValue.currentTarget.value);
                    }}
                    InputProps={{ classes: { error: runtimeFieldClasses.error, root: runtimeFieldClasses.root } }}
                    FormHelperTextProps={{ classes: { root: runtimeFieldClasses.helperText, error: runtimeFieldClasses.errorHelperText } }}
                />
            )}
        />
    );
};
